import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { WeathermapFacade } from '@store/weathermap.facade';
import { PinType, RainScale, TemperatureQuality, WindType } from '@store/weathermap.store';
import { RequestParameters } from 'maplibre-gl';
import { WeathermapTokenService } from 'src/app/services/weathermap-token.service';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const vtpbf = require('vt-pbf');
// eslint-disable-next-line @typescript-eslint/no-require-imports
const geojsonvt = require('geojson-vt');

interface StationData {
  _id: string;
  place: {
    location: [number, number];
    altitude: number;
    city: string;
    street: string;
  },
  measures: Record<string, ModuleMeasures>;
  module_types: Record<string, string>;
}

type ModuleMeasures = OutoorModuleMeasures | WindModuleMeasures | IndoorModuleMeasures | RainModuleMeasures;

interface OutoorModuleMeasures {
  res: {
    string: [number, number],
    type: ['temperature', 'humidity']
  }
}

interface WindModuleMeasures {
  wind_strength: number;
  wind_angle: number;
  gust_strength: number;
  gust_angle: number;
  wind_timeutc: number;
}

interface IndoorModuleMeasures {
  res: {
    string: [number],
    type: ['pressure']
  }
}

interface RainModuleMeasures {
  rain_60min: number;
  rain_24h: number;
  rain_live: number;
  rain_timeutc: number;
}

interface WindModuleMeasures {
  wind_strength: number;
  wind_angle: number;
  gust_strength: number;
  gust_angle: number;
  wind_timeutc: number;
}

interface Bounds {
  sw: { lat: number; lng: number; };
  ne: { lat: number; lng: number; };
}

interface Point {
  x: number;
  y: number;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface Tile {
  x: number;
  y: number;
  z: number;
}

function fromPointToLatLng(point: Point): LatLng {
  return {
    lat: (2 * Math.atan(Math.exp((point.y - 128) / -(256 / (2 * Math.PI)))) - Math.PI / 2) / (Math.PI / 180),
    lng: (point.x - 128) / (256 / 360),
  };
}

function getTileBounds(tile: Tile): Bounds {
  tile = normalizeTile(tile);

  const t = Math.pow(2, tile.z);
  const s = 256 / t;
  const sw = {
    x: tile.x * s,
    y: (tile.y * s) + s,
  };
  const ne = {
    x: tile.x * s + s,
    y: (tile.y * s),
  };

  return {
    sw: fromPointToLatLng(sw),
    ne: fromPointToLatLng(ne),
  };
}

function normalizeTile(tile: Tile): Tile {
  const t = Math.pow(2, tile.z);
  tile.x = ((tile.x % t) + t) % t;
  tile.y = ((tile.y % t) + t) % t;
  return tile;
}

interface ProtocolDescription {
  data: {
    scheme: string,
    tiles: string[],
    minzoom: number,
    maxzoom: number,
  },
}

interface TileData {
  data: Uint8Array;
}

@Injectable({
  providedIn: 'root',
})
export class NAProtocolService {
  private readonly weathermapFacade = inject(WeathermapFacade);
  private readonly wetahermapTokenService = inject(WeathermapTokenService);

  tile: maplibregl.AddProtocolAction = async (args: RequestParameters, abortController: AbortController): Promise<ProtocolDescription | TileData> => {
    if (args.type === 'json') {
      const re = new RegExp(/NAProtocol:\/\/(temperature|rain|wind)/);
      const result = args.url.match(re);

      const pinType = result[1];

      return {
        data: {
          scheme: 'xyz',
          tiles: [`NAProtocol://${pinType}/{z}/{x}/{y}`],
          minzoom: 0,
          maxzoom: 14,
        },
      };
    }

    const re = new RegExp(/NAProtocol:\/\/(temperature|rain|wind)\/(\d+)\/(\d+)\/(\d+)/);
    const result = args.url.match(re);

    const tileCoords = {
      z: +result[2],
      x: +result[3],
      y: +result[4],
    };

    const bounds = getTileBounds(tileCoords);

    const additionalParams = new URLSearchParams({
      zoom: tileCoords.z.toString(),
      lat_ne: bounds.ne.lat.toString(),
      lon_ne: bounds.ne.lng.toString(),
      lat_sw: bounds.sw.lat.toString(),
      lon_sw: bounds.sw.lng.toString(),
      date_end: 'last',
      access_token: this.wetahermapTokenService.token(),
    });

    const pinType = result[1] as PinType;
    const params = this.getParams(pinType, tileCoords.z);

    const response = await fetch(`${environment.apiUrl}getpublicmeasures?${params}&${additionalParams}`, { signal: abortController.signal });
    const data: {body: StationData[]} = await response.json();

    const temperatureUnit = this.weathermapFacade.unitSystem() ? 'imperial' : 'metric';

    const tileindex = geojsonvt.default({
      type: 'FeatureCollection',
      features: data?.body?.map((station: StationData) => {
        const modules = Object.entries(station.module_types)
          .map(([id, type]) => ({ id, type }));

        const properties: {
          zoomLevel: number;
          temperature?: number;
          rain_24h?: number;
          rain_60min?: number;
          rain_live?: number;
          rainScale?: RainScale;
          wind_strength?: number,
          wind_angle?: number,
          gust_strength?: number,
          gust_angle?: number,
          wind?: number,
          angle?: number,
          windType?: WindType,
          temperatureUnit: string,
        } = {
          zoomLevel: tileCoords.z,
          temperatureUnit,
        };

        switch (pinType) {
          case 'temperature': {
            const outdoorModule = modules.find(m => m.type === 'NAModule1');
            const outdoorMeasures = station.measures[outdoorModule.id] as OutoorModuleMeasures;
            const time = Object.keys(outdoorMeasures.res ?? {})[0];
            properties.temperature = Math.round(outdoorMeasures.res?.[time]?.[0]);
            break;
          }
          case 'rain': {
            const rainModule = modules.find(m => m.type === 'NAModule3');
            const rainMeasures = station.measures[rainModule.id] as RainModuleMeasures;
            properties.rain_24h = rainMeasures.rain_24h;
            properties.rain_60min = rainMeasures.rain_60min;
            properties.rain_live = rainMeasures.rain_live;
            properties.rainScale = this.weathermapFacade.rainScale();
            break;
          }
          case 'wind': {
            const windModule = modules.find(m => m.type === 'NAModule2');
            const windMeasures = station.measures[windModule.id] as WindModuleMeasures;
            properties.wind_strength = windMeasures.wind_strength;
            properties.wind_angle = windMeasures.wind_angle;
            properties.gust_strength = windMeasures.gust_strength;
            properties.gust_angle = windMeasures.gust_angle;
            properties.windType = this.weathermapFacade.windType();
            break;
          }
        }

        return {
          type: 'Feature',
          properties: {
            id: station._id,
            city: station.place.city ?? '',
            street: station.place.street ?? '',
            altitude: station.place.altitude ?? 0,
            modules,
            ...properties,
          },
          geometry: {
            type: 'Point',
            coordinates: station.place.location,
          },
        };
      }).filter(t => !!t) ?? [],
    }, {});

    const tile = tileindex.getTile(
      tileCoords.z,
      tileCoords.x,
      tileCoords.y,
    );

    if (!tile) {
      return {
        data: new Uint8Array(),
      };
    }

    const buff = vtpbf.fromGeojsonVt({ stations: tile });

    return {
      data: buff,
    };
  };

  private getParams(pinType: PinType, z: number): URLSearchParams {
    switch (pinType) {
      case 'temperature': {
        return this.getTemperatureParams(z);
      }
      case 'rain': {
        return this.getRainParams(z);
      }
      case 'wind': {
        return this.getWindParams(z);
      }
    }
  }

  private getTemperatureParams(z: number): URLSearchParams {
    const limit = z > 4 ? 1 : 2;
    const divider = z > 4 ? 7 : 10;

    const temperatureQuality = this.weathermapFacade.temperatureQuality();
    const quality = temperatureQuality === TemperatureQuality.LOW ? 1 : 7;

    return new URLSearchParams({
      limit: limit.toString(),
      divider: divider.toString(),
      quality: quality.toString(),
    });
  }

  private getRainParams(z: number): URLSearchParams {
    const limit = z > 4 ? 1 : 2;
    const divider = z > 4 ? 7 : 10;

    return new URLSearchParams({
      limit: limit.toString(),
      divider: divider.toString(),
      required_module: 'NAModule3',
    });
  }

  private getWindParams(z: number): URLSearchParams {
    const limit = z > 4 ? 1 : 2;
    const divider = z > 4 ? 7 : 10;
    return new URLSearchParams({
      limit: limit.toString(),
      divider: divider.toString(),
      required_module: 'NAModule2',
    });
  }
}
